import { mergeCardSnapshots, rememberSupportSnapshot } from "./business-cards";
import type {
  SupportRequestFields,
  SupportRequestCommand,
  SupportRequestBlockData,
} from "@go-novus/admin-api";
import {
  supportRequestCardSchema,
  type SupportRequestCard,
} from "@go-novus/admin-api";

export function supportCards(
  cards: unknown[] | undefined,
): SupportRequestCard[] {
  return (cards ?? []).flatMap((card) => {
    const parsed = supportRequestCardSchema.safeParse(card);
    return parsed.success ? [parsed.data] : [];
  });
}

/** 每个通道共享最高版本；先读取现有快照，相同版本仅允许补充被持久快照剥离的邮件全文。 */
export function mergeSupportCards<
  T extends { supportRequests?: SupportRequestCard[] },
>(previous: T[], incoming: T[], updates: SupportRequestCard[] = []): T[] {
  return mergeCardSnapshots(
    previous,
    incoming,
    updates,
    (row) => row.supportRequests,
    (row, cards) => ({ ...row, supportRequests: cards }),
    (prior, card) =>
      prior.lifecycle === "active" &&
      card.lifecycle === "active" &&
      !prior.payload.email &&
      !!card.payload.email &&
      JSON.stringify({ ...prior.payload, email: undefined }) ===
        JSON.stringify({ ...card.payload, email: undefined }),
  );
}

/** 保存或冲突响应更新基准版本，保留等待期间的新输入。 */
export function reconcileSupportEdits(
  edits:
    | Record<string, { version: number; fields: SupportRequestFields }>
    | undefined,
  card: SupportRequestCard,
  command: SupportRequestCommand,
  successful: boolean,
  conflicted = false,
) {
  const requestId = card.payload.request_id;
  const current = edits?.[requestId];
  const submitted =
    command.operation === "save" || command.operation === "pause";
  const clear =
    card.lifecycle !== "active" ||
    (successful &&
      submitted &&
      JSON.stringify(current?.fields) === JSON.stringify(command.fields));
  const next = Object.fromEntries(
    Object.entries(edits ?? {}).filter(([id]) => !clear || id !== requestId),
  );
  if (
    !clear &&
    current &&
    (conflicted ||
      (successful && (submitted || command.operation === "resume")))
  )
    next[requestId] = {
      ...current,
      version: Math.max(current.version, card.payload.content_version),
    };
  return next;
}

/** 尚未收到原消息的更新也要记住；重连/乱序不能在稍后恢复已过期内容。 */
export function rememberSupportUpdate(
  previous: SupportRequestCard[] | undefined,
  card: SupportRequestCard,
) {
  return rememberSupportSnapshot(previous, card);
}

/** 块没有旧卡信封；仅为复用采集组件构造内存视图，不写入快照。 */
export function supportBlockCard(
  data: SupportRequestBlockData,
): SupportRequestCard {
  const expired = data.phase === "expired";
  return supportRequestCardSchema.parse({
    card_id: `support:${data.request_id}`,
    type: "support_request",
    version: expired ? 1 : data.content_version,
    lifecycle: expired ? "expired" : "active",
    updated_at: expired
      ? new Date().toISOString()
      : (data.accepted_at ?? data.expires_at),
    payload: expired ? { request_id: data.request_id } : data,
  });
}
export function supportCardData(
  card: SupportRequestCard,
): SupportRequestBlockData {
  if (card.lifecycle !== "active")
    return { request_id: card.payload.request_id, phase: "expired" };
  const { email: _email, ...payload } = card.payload;
  return payload;
}
